import { clamp } from 'lodash';
import type { Rect, Size, TourPlacement } from './types';

/**
 * Where the tour card sits relative to the element a step points at. Nothing
 * here touches the spotlight — see `spotlightGeometry.ts` for the dimming layer
 * and the ring drawn around the target.
 */

/** Gap between the target element and the popover, in pixels. */
const GAP = 16;
/** Minimum distance the popover keeps from the viewport edges, in pixels. */
const MARGIN = 12;

export type PopoverPosition = {
  top: number;
  left: number;
  placement: TourPlacement;
};

type Fits = Record<TourPlacement, boolean>;

const getFits = (
  target: Rect,
  popover: Size,
  vw: number,
  vh: number
): Fits => ({
  top: target.top - GAP - popover.height >= MARGIN,
  bottom: target.top + target.height + GAP + popover.height <= vh - MARGIN,
  left: target.left - GAP - popover.width >= MARGIN,
  right: target.left + target.width + GAP + popover.width <= vw - MARGIN,
});

const FALLBACK_ORDER: TourPlacement[] = ['bottom', 'top', 'right', 'left'];

const resolvePlacement = (
  preferred: TourPlacement,
  fits: Fits
): TourPlacement => {
  if (fits[preferred]) return preferred;
  return FALLBACK_ORDER.find(placement => fits[placement]) ?? preferred;
};

/**
 * Computes a viewport-fixed position for the popover relative to the target.
 * Flips to a fitting side when the preferred placement would overflow, then
 * clamps within the viewport so the popover is always fully visible.
 */
export const computePopoverPosition = (
  target: Rect,
  popover: Size,
  preferred: TourPlacement
): PopoverPosition => {
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  const placement = resolvePlacement(
    preferred,
    getFits(target, popover, vw, vh)
  );

  const alignedX = target.left + target.width / 2 - popover.width / 2;
  const alignedY = target.top + target.height / 2 - popover.height / 2;

  const byPlacement: Record<TourPlacement, { top: number; left: number }> = {
    bottom: { top: target.top + target.height + GAP, left: alignedX },
    top: { top: target.top - GAP - popover.height, left: alignedX },
    right: { top: alignedY, left: target.left + target.width + GAP },
    left: { top: alignedY, left: target.left - GAP - popover.width },
  };

  const { top, left } = byPlacement[placement];

  return {
    top: clamp(top, MARGIN, vh - popover.height - MARGIN),
    left: clamp(left, MARGIN, vw - popover.width - MARGIN),
    placement,
  };
};
